import type { SorterResult } from "antd/es/table/interface";

import { getMapperFor } from "../../../core/mappers/Mapper";
import { baseAuthApi } from "../../../core/service/baseApi";
import type { Dto } from "../../../core/types/be/Dto";
import type { ResourcePageDto } from "../../../core/types/be/ResourcePageDto";
import type { FetchAutoCompleteDataQuery, FetchDataQuery, SpecificTableData, UpdateDataQuery } from "../types/types";
import { getExportFilename } from "./exportUtils";

export const tableApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        getTableData: builder.query<ResourcePageDto<Dto>, FetchDataQuery>({
            providesTags: ["mainCache"],
            query: ({ resourceFetchPath, query }) => {
                return `${resourceFetchPath}?${query}`;
            },
            transformResponse: (response: ResourcePageDto<Dto>, _, query): object => {
                const map = getMapperFor(query.dataMapperKey);
                return map(response);
            },
        }),
        getSearchAutoComplete: builder.query<ReadonlyArray<object>, FetchAutoCompleteDataQuery>({
            providesTags: ["mainCache"],
            query: ({ resourceFetchPath, query }) => {
                return `${resourceFetchPath}?${query}`;
            },
            transformResponse: (response: ResourcePageDto<Dto>, _, query) => {
                if (query.dataMapperKey) {
                    const map = getMapperFor(query.dataMapperKey);
                    return (map(response) as SpecificTableData).list;
                }
                return (response as SpecificTableData).list;
            },
        }),
        exportTableData: builder.mutation<unknown, UpdateDataQuery>({
            query: ({ url, method, body, downloadFileName }) => ({
                url,
                method,
                body,
                responseHandler: async (response) => {
                    if (!response.ok) {
                        // Backend may fail to return valid JSON (e.g. outage) - fall back to statusText instead
                        // of letting the raw JSON parse failure crash the handler.
                        let message = response.statusText || `Export failed: ${response.status}`;
                        try {
                            const error = await response.json();
                            if (error?.message) message = error.message;
                        } catch {
                            // ignore - keep the fallback message
                        }
                        throw new Error(message);
                    }

                    const contentDisposition = response.headers.get("Content-Disposition");
                    let filename = getExportFilename(downloadFileName);

                    if (contentDisposition) {
                        const match = contentDisposition.match(/filename="?(.+)"?/);
                        if (match) {
                            const [, extractedFilename] = match;
                            if (extractedFilename) {
                                filename = extractedFilename;
                            }
                        }
                    }

                    const a = window.document.createElement("a");
                    a.href = window.URL.createObjectURL(await response.blob());
                    a.download = filename;

                    document.body.appendChild(a);
                    a.click();

                    document.body.removeChild(a);
                },
                cache: "no-cache",
            }),
        }),
        updateTableData: builder.mutation<undefined, UpdateDataQuery>({
            query: ({ url, method, body }) => ({
                url,
                method,
                body,
            }),
            invalidatesTags: ["mainCache"],
        }),
        loadDatasourceConfigurations: builder.query<{ items: Record<string, string>[] }, string>({
            providesTags: ["mainCache"],
            query: (configKey) => `/configurations/${configKey}`,
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetTableDataQuery,
    useGetSearchAutoCompleteQuery,
    useUpdateTableDataMutation,
    useExportTableDataMutation,
    useLoadDatasourceConfigurationsQuery,
} = tableApi;

export type QueryParamsConfig<T> = {
    pageSize: number | undefined;
    currentPage: number | undefined;
    filters: T;
    sorter: SorterResult<T> | SorterResult<T>[];
};

export function createQueryParams<T>(config: QueryParamsConfig<T>): string {
    const searchParams = new URLSearchParams();

    if (config.sorter && !Array.isArray(config.sorter) && config.sorter.order) {
        const order = config.sorter.order === "ascend" ? "asc" : "desc";
        // BE sorts by its entity property name, which can differ from the FE column's dataIndex
        const sortField = (config.sorter.column as { sortField?: string } | undefined)?.sortField ?? config.sorter.field;
        searchParams.set("orderBy", `${order}(${sortField})`);
    } else {
        searchParams.set("orderBy", `desc(changed)`);
    }

    if (config.filters) {
        Object.values(config.filters).forEach((filter, idx) => {
            if (filter) {
                searchParams.set(Object.keys(config.filters as object)[idx], String(filter));
            }
        });
    }

    searchParams.set("pageSize", String(config.pageSize || 10));
    // API endpoint expects pagination to start from 0.
    searchParams.set("pageNumber", String((config.currentPage || 1) - 1));

    return searchParams.toString();
}
